import * as React from "react";
import { useEffect, useState } from "react";
import { devices, TranslationSteps } from "~/Common/TranslationConstant";
import { Dictionary } from "~/Types/Common";
import { useSaveContentValue, useTranslationSingleValue, TranslateSingleValue } from "~/Hooks/Api/Translations";
import { react2Angular } from "~/Core/React2Angular";
import { LoadingSpinner } from "./LoadingSpinner";
import { SimpleModal } from "./SimpleModal";
import { TS } from "~/Services/TS";
import { Content } from "~/Generated/Entities";
import { useCurrentUserContext } from "~/Contexts/CurrentUserContext";
import { CKEditor } from "ckeditor4-react";
import { useGetLanguages, useGetPersonas } from "~/Generated/OData";

const HtmlEditor = ({
    htmlContent,
    editorRef,
    setEditorRef,
}: {
    htmlContent?: string;
    editorRef: any;
    setEditorRef: (editor: any) => void;
}) => {
    const editorConfig = {
        autoParagraph: false,
        allowedContent: true,
        extraPlugins: "bidi, colorbutton, floatpanel, font, imagemaps, justify, panelbutton, showblocks, switchbar",
        toolbar: "Full",
        fullPage: true,
    };
    useEffect(() => {
        if (editorRef && htmlContent) {
            editorRef.setData(htmlContent);
        }
    }, [htmlContent]);
    const instanceReadyHandler = (evt: any) => {
        (window as any).CKFinder.setupCKEditor(evt.editor);
        setEditorRef(evt.editor);
    };
    return <CKEditor onInstanceReady={instanceReadyHandler} config={editorConfig} />;
};

export const GenerateTranslationModal = ({
    isModalOpen,
    languageId,
    contentManagerId,
    htmlContent,
    onCloseModal,
    onReloadPage,
}: {
    isModalOpen: boolean;
    languageId: string;
    contentManagerId: string;
    htmlContent: string;
    onCloseModal: () => void;
    onReloadPage: () => void;
}) => {
    const { data: languages } = useGetLanguages();
    const { data: personas } = useGetPersonas();
    const translationSingleMutate = useTranslationSingleValue();
    const saveContentValueMutate = useSaveContentValue();
    const currentLanguageLookups = languages ? languages.value.filter(x => x.id !== languageId) : [];
    const personaLookups = personas
        ? personas.value
              .filter(o => {
                  return o.deactivateOn === null || Date.parse(new Date().toDateString()) < Date.parse(o.deactivateOn);
              })
              .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
        : [];
    const defaultId = personaLookups.find(x => x.isDefault)?.id;
    const [currentStep, setCurrentStep] = useState<string>(TranslationSteps.CHOOSE_LANGUAGE);
    const [stepIndex, setStepIndex] = React.useState<number>(0);
    const [selectedLanguageCodes, setSelectedLanguages] = useState<string[]>([]);
    const [isSaveButtonVisible, setIsSaveButtonVisible] = useState<boolean>(false);
    const [languageSelected, setLanguageSelected] = useState<string>("");
    const [translationValues, setTranslationValue] = useState<Dictionary<Partial<Content>>>({});
    const [editorRef, setEditorRef] = useState<any>(null);
    const [isTranslating, setIsTranslating] = useState<boolean>(false);
    const [isNextButtonDisabled, setIsNextButtonDisabled] = useState<boolean>(false);
    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    let { id: currentUserId } = useCurrentUserContext()!;

    const handleCloseModal = () => {
        resetState();
        onCloseModal();
    };
    const onSelectLanguage = (languageCode: string) => {
        const currentSelectedLanguages = selectedLanguageCodes;
        const findIdx = currentSelectedLanguages.indexOf(languageCode);
        if (findIdx > -1) {
            currentSelectedLanguages.splice(findIdx, 1);
        } else {
            currentSelectedLanguages.push(languageCode);
        }
        setSelectedLanguages([...currentSelectedLanguages]);
    };
    const onHandleContinue = () => {
        setIsTranslating(true);
        setIsNextButtonDisabled(true);
        setIsSaveButtonDisabled(true);
        const sourceLanguageCode = languages?.value.find(x => x.id == languageId)?.languageCode;
        if (sourceLanguageCode) {
            const paramRequest: TranslateSingleValue = {
                originalValue: htmlContent,
                sourceLanguage: sourceLanguageCode,
                targetLanguages: selectedLanguageCodes,
            };
            translationSingleMutate.mutate(paramRequest, {
                onSuccess: response => {
                    if (response.isSuccess) {
                        const contentDictionary: Dictionary<Partial<Content>> = {};
                        const { translationValues } = response.value!;
                        Object.keys(translationValues).map(key => {
                            const languageId = currentLanguageLookups.find(x => x.languageCode === key)?.id;
                            if (languageId) {
                                var value = translationValues[key] ?? "";
                                const content: Partial<Content> = {
                                    languageId: languageId,
                                    personaId: defaultId ?? "",
                                    deviceType: devices[0],
                                    contentManagerId: contentManagerId,
                                    createdByAdminUserProfileId: currentUserId,
                                    html: value,
                                    revision: 1,
                                    name: "New Revision",
                                    generationType: "Manual",
                                };
                                contentDictionary[key] = content;
                            }
                        });
                        setTranslationValue({ ...contentDictionary });
                        setIsTranslating(false);
                        setIsNextButtonDisabled(false);
                        setIsSaveButtonDisabled(false);
                        if (Object.keys(contentDictionary)?.length === 1) {
                            setIsSaveButtonVisible(true);
                        }
                        setLanguageSelected(selectedLanguageCodes[stepIndex]);
                    } else {
                        if (response?.value?.errorMessage) {
                            setErrorMessage(response?.value?.errorMessage);
                        }
                    }
                },
            });
        }
        setCurrentStep(TranslationSteps.EDIT_TRANSLATION);
    };
    const onNextButton = () => {
        setValueTranslationContent();
        if (stepIndex === selectedLanguageCodes.length - 2) {
            setIsSaveButtonVisible(true);
        }
        setStepIndex(stepIndex + 1);
        setLanguageSelected(selectedLanguageCodes[stepIndex + 1]);
    };
    const onBackButton = () => {
        setIsSaveButtonVisible(false);
        setValueTranslationContent();
        if (stepIndex - 1 < 0) {
            setCurrentStep(TranslationSteps.CHOOSE_LANGUAGE);
            setStepIndex(0);
            setTranslationValue({});
        } else {
            setStepIndex(stepIndex - 1);
            setLanguageSelected(selectedLanguageCodes[stepIndex - 1]);
        }
    };
    const onSaveButton = () => {
        setValueTranslationContent();
        const languageSelectedId = currentLanguageLookups.find(x => x.languageCode === languageSelected)?.id;
        if (languageSelectedId) {
            Object.keys(translationValues).map(language => {
                const paramRequest = translationValues[language]!;
                saveContentValueMutate.mutate(paramRequest, {
                    onSuccess: data => {
                        onReloadPage();
                        handleCloseModal();
                    },
                });
                setIsSaveButtonDisabled(true);
            });
        }
    };
    const onChangeDevice = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const content = translationValues[languageSelected];
        if (content && Object.hasOwn(content, "deviceType")) {
            content!.deviceType = value;
            setTranslationValue({ ...translationValues, [languageSelected]: content });
        }
    };
    const onChangeSegment = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const content = translationValues[languageSelected];
        if (content && Object.hasOwn(content, "personaId")) {
            content!.personaId = value;
            setTranslationValue({ ...translationValues, [languageSelected]: content });
        }
    };
    const onChangeLanguage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setValueTranslationContent();
        setLanguageSelected(event.target.value);
        const findLanguageIndex = selectedLanguageCodes.findIndex(x => x === event.target.value);
        if (findLanguageIndex === selectedLanguageCodes.length - 1) {
            setIsSaveButtonVisible(true);
        } else {
            setIsSaveButtonVisible(false);
        }
        setStepIndex(findLanguageIndex);
    };
    const resetState = () => {
        setCurrentStep(TranslationSteps.CHOOSE_LANGUAGE);
        setStepIndex(0);
        setSelectedLanguages([]);
        setIsSaveButtonVisible(false);
        setLanguageSelected("");
        setTranslationValue({});
        setEditorRef(null);
        setIsTranslating(false);
        setIsNextButtonDisabled(false);
        setIsSaveButtonDisabled(false);
        setErrorMessage("");
    };
    const setValueTranslationContent = () => {
        const value = editorRef.getData();
        const content = translationValues[languageSelected];
        if (content && Object.hasOwn(content, "html")) {
            content!.html = value;
            setTranslationValue({ ...translationValues, [languageSelected]: content });
        }
    };
    const onCloseErrorModal = () => {
        setErrorMessage("");
        setIsTranslating(false);
    };
    return (
        <div>
            <div
                className={`modal-overlay modal--large content-manager-generate-translation-modal ${isModalOpen && "is-active"}`}
                zf-modal="autoTranslateModal"
                zf-closable="modal"
                id="autoTranslateModal"
            >
                <aside className="modal">
                    <div>
                        <div className="modal-header">
                            <h4 className="modal-title">{TS("Generate Translation")} </h4>
                            <button className="close-button" onClick={handleCloseModal}>
                                <i className="fa-light fa-xmark"></i>
                            </button>
                        </div>
                        <div className="modal-content container">
                            <section className="step-wizard">
                                <ul className="step-wizard-list">
                                    <li className="step-wizard-item">
                                        <span className="progress-count"></span>
                                        <span className="progress-label">{TS("Choose Languages")}</span>
                                    </li>
                                    <li
                                        className={`step-wizard-item ${currentStep !== TranslationSteps.EDIT_TRANSLATION && "current-item"}`}
                                    >
                                        <span className="progress-count"></span>
                                        <span className="progress-label">{TS("Edit Translation")}</span>
                                    </li>
                                </ul>
                            </section>
                            {isTranslating && <LoadingSpinner />}
                            {currentStep === TranslationSteps.CHOOSE_LANGUAGE && (
                                <div className="choose-language">
                                    <span className="title">{TS("Choose languages to translate")} </span>
                                    <ul className="language-list">
                                        {currentLanguageLookups.map(({ id, languageCode, description }) => (
                                            <li key={id}>
                                                <div className="language-list-item">
                                                    <div className="language-section">
                                                        <input
                                                            type="checkbox"
                                                            id={id}
                                                            name={description}
                                                            value={languageCode}
                                                            onChange={() => onSelectLanguage(languageCode)}
                                                            checked={selectedLanguageCodes.includes(languageCode)}
                                                        />
                                                        <label htmlFor={id}>{description}</label>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {currentStep === TranslationSteps.EDIT_TRANSLATION && (
                                <div className="grid-block">
                                    <div className="grid-block vertical filter">
                                        <div className="title-bar whitebg">
                                            <span className="left">
                                                <span className="inline-label">
                                                    <label>{TS("Language")} </label>
                                                    <select onChange={onChangeLanguage} value={languageSelected}>
                                                        {selectedLanguageCodes.map((languageCode: string) => {
                                                            const description =
                                                                currentLanguageLookups.find(
                                                                    x => x.languageCode == languageCode,
                                                                )?.description || "";
                                                            return (
                                                                <option key={languageCode} value={languageCode}>
                                                                    {languageCode} - {description}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </span>
                                                <span className="inline-label">
                                                    <label>{TS("Customer Segment")}</label>
                                                    <select
                                                        onChange={onChangeSegment}
                                                        value={translationValues[languageSelected]?.personaId}
                                                    >
                                                        {personaLookups.map(({ id, name }) => (
                                                            <option key={id} value={id}>
                                                                {name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </span>
                                                <span className="inline-label">
                                                    <label>{TS("Device")}</label>
                                                    <select
                                                        onChange={onChangeDevice}
                                                        value={translationValues[languageSelected]?.deviceType}
                                                    >
                                                        {devices.map(device => (
                                                            <option key={device} value={device}>
                                                                {device}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </span>
                                            </span>
                                        </div>
                                        <div className="grid-block">
                                            <div className="ckeditor-container">
                                                <HtmlEditor
                                                    htmlContent={
                                                        translationValues[selectedLanguageCodes[stepIndex]]?.html ?? ""
                                                    }
                                                    editorRef={editorRef}
                                                    setEditorRef={setEditorRef}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <div>
                                <button className="button tertiary" onClick={handleCloseModal}>
                                    {TS("Cancel")}
                                </button>
                                {currentStep === TranslationSteps.CHOOSE_LANGUAGE && (
                                    <button
                                        disabled={selectedLanguageCodes.length === 0}
                                        className="button"
                                        onClick={onHandleContinue}
                                    >
                                        {TS("Continue")}
                                    </button>
                                )}
                                {currentStep === TranslationSteps.EDIT_TRANSLATION && (
                                    <>
                                        <button className="button tertiary" onClick={onBackButton}>
                                            {TS("Back")}
                                        </button>
                                        {!isSaveButtonVisible ? (
                                            <button
                                                disabled={isNextButtonDisabled}
                                                className="button tertiary"
                                                onClick={onNextButton}
                                            >
                                                {TS("Next")}
                                            </button>
                                        ) : (
                                            <button
                                                className="button"
                                                disabled={isSaveButtonDisabled}
                                                onClick={onSaveButton}
                                            >
                                                {TS("Save")}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
            <SimpleModal
                isModalOpen={errorMessage !== ""}
                title={TS("Error")}
                onCloseModal={onCloseErrorModal}
                message={errorMessage}
            />
        </div>
    );
};

angular
    .module("insite-admin")
    .component(
        "generateTranslationModal",
        react2Angular(GenerateTranslationModal, [
            "isModalOpen",
            "languageId",
            "contentManagerId",
            "htmlContent",
            "onCloseModal",
            "onReloadPage",
        ]),
    );
